import type { SectionId } from '#src/app/pages/petitionDetails/shared/hooks';
import { focus } from '#src/app/shared/utils/dom';

import { getVideoThumbnail, type VideoThumbnailOptions } from './videoThumbnail';

/**
 * Generic video type that only requires the videoUrl property.
 * This is compatible with various video types in the codebase (GraphQL results, SupporterVideo, etc.)
 */
type VideoWithUrl = {
	readonly videoUrl: string;
};

/**
 * Filters supporter videos to get only those with valid video URLs.
 * Useful for getting video comments that can display thumbnails.
 *
 * @param videos - Array of videos with videoUrl property
 * @returns Array of videos with valid videoUrl
 */
export function filterVideoComments<T extends VideoWithUrl>(videos: readonly T[]): T[] {
	return videos.filter((video) => video.videoUrl && video.videoUrl.length > 0);
}

/**
 * Gets the first usable video thumbnail from an array of supporter videos.
 * Iterates in order and returns the thumbnail URL for the first video that yields
 * a thumbnail (e.g. Mux). This ensures mixed-source lists still
 * show the preview when a later video has a valid thumbnail.
 *
 * @param videos - Array of videos with videoUrl property
 * @param options - Optional parameters for thumbnail customization
 * @returns The first available thumbnail URL, or undefined if none of the videos support thumbnails
 */
export function getFirstVideoThumbnail(
	videos: readonly VideoWithUrl[],
	options?: VideoThumbnailOptions,
): string | undefined {
	const videoComments = filterVideoComments(videos);

	for (const video of videoComments) {
		const thumbnail = getVideoThumbnail(video.videoUrl, options);
		if (thumbnail) {
			return thumbnail;
		}
	}
	return undefined;
}

/**
 * Checks if there are any video comments available.
 *
 * @param videos - Array of videos with videoUrl property
 * @returns True if there is at least one video with a valid URL
 */
export function hasVideoComments(videos: readonly VideoWithUrl[]): boolean {
	return filterVideoComments(videos).length > 0;
}

/**
 * Scrolls to a petition-details section and moves focus to it, making it focusable first
 * if needed. Shared by momentum bar handlers that both scroll to and focus a section on click.
 *
 * @param sectionId - The section to scroll to and focus
 * @param scrollToSection - The scrollToSection action from useScrollToSection
 */
export function scrollToSectionAndFocus(sectionId: SectionId, scrollToSection: (id: SectionId) => void): void {
	scrollToSection(sectionId);
	focus(document.querySelector<HTMLElement>(`#${sectionId}`), {
		makeFocusable: true,
		preventScroll: true,
	});
}
